import React, { useCallback, useMemo } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import styled from 'styled-components';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import { KLAVIYO_PPC_IE, MEDIA } from '@constants';
import { BadgeSizeType, PageSlice, SliceHeaderImageDetails } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import useTranslate from '@hooks/useTranslate';
import useNavigate from '@utils/useNavigate';
import useProductPrice from '@utils/useProductPrice';
import Badge from './Badge';
import StorefrontModuleHead from './design/StorefrontModuleHead';
import { TextVariant, TEXT_STYLES, Typography } from './design/Typography';
import EmailCapture from './EmailCapture';
import LazyLoadImage from './LazyLoadImage';
import PriceDisplay from './PriceDisplay';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
}

const HeaderImage = ({ slice }: Props) => {
  const { t } = useTranslate();
  const { navigateWrapper } = useNavigate();
  const { country } = useCountry();
  const sliceDetails = slice.primary as SliceHeaderImageDetails;
  const isMobile = !useDesktop(1024);
  const image = isMobile ? sliceDetails.mobile_image || sliceDetails.image : sliceDetails.image;
  const isFsaHsaEligible = country === CountryCode.US && sliceDetails.fsa_hsa_eligible;
  const productCode = sliceDetails.product_code;
  const productPrice = useProductPrice(productCode);
  const pinImageRight = sliceDetails.pin_image_right ?? false;
  const onLink1Click = useCallback(() => {
    if (!sliceDetails.button_url) return;
    navigateWrapper(sliceDetails.button_url);
  }, []);

  const waitlistId = useMemo(() => {
    if (sliceDetails.waitlist_code) {
      return sliceDetails.waitlist_code;
    }

    if (country === CountryCode.US) {
      return sliceDetails.waitlist_us || '';
    }

    // Hide waitlist for PPC in Ireland
    if (country === CountryCode.IE && sliceDetails.waitlist_eu === KLAVIYO_PPC_IE) {
      return '';
    }

    if (country !== CountryCode.GB) {
      return sliceDetails.waitlist_eu || '';
    }

    return '';
  }, [country, sliceDetails.waitlist_code, sliceDetails.waitlist_us, sliceDetails.waitlist_eu]);

  const renderButtonOrWaitlist = () => {
    if (waitlistId) {
      return (
        <WaitlistWrap>
          <Typography $variant={TextVariant.BodyBoldMD}>{t('components.first-to-know')}</Typography>
          <EmailCapture id={waitlistId} background={Colors.cream} textColor={Colors.navyBlue} />
        </WaitlistWrap>
      );
    }

    return sliceDetails.button_url ? (
      <>
        {productPrice && (
          <OnlyFor>
            {t('common.only-for')}: {<PriceDisplay price={productPrice} />}
          </OnlyFor>
        )}
        <ButtonRow>
          <Button onClick={onLink1Click}>{sliceDetails.button_text_key}</Button>
        </ButtonRow>
      </>
    ) : null;
  };

  return (
    <Container $background={sliceDetails.background_color || 'linear-gradient(180deg, #f9d07c 0%, #fffbe7 100%)'}>
      <SlideInner $pinImageRight={pinImageRight}>
        <ContentContainer $centeredText={sliceDetails.centered_text ?? false} data-testid="content-container">
          {sliceDetails?.coming_soon && (
            <ComingSoonWrap>
              <Badge sizeTypes={[BadgeSizeType.Small]} text={t('components.coming-soon')} />
            </ComingSoonWrap>
          )}
          <StorefrontModuleHead
            centeredText={sliceDetails.centered_text}
            overline={sliceDetails.overline}
            heading={sliceDetails.heading}
            subheading={sliceDetails.subheading}
          />
          {renderButtonOrWaitlist()}
          {isFsaHsaEligible && <FSAHSANotice>{t('components.fsa-hsa-notice')}</FSAHSANotice>}
        </ContentContainer>
        <ImageContainer $pinImageRight={pinImageRight}>{image?.url && <LazyLoadImage src={image.url} alt={image.alt ?? ''} />}</ImageContainer>
      </SlideInner>
    </Container>
  );
};

export default HeaderImage;

interface ImageProps {
  $pinImageRight: boolean;
}

const Container = styled.div<{ $background: string }>`
  width: 100%;
  background: ${(props) => props.$background};
`;

const SlideInner = styled.div<ImageProps>`
  width: 100%;
  max-width: ${(props) => (props.$pinImageRight ? '100%' : '1440px')};
  height: 650px;
  padding-left: 120px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  margin: 0 auto;
  position: relative;
  z-index: 2;

  ${MEDIA.laptop} {
    height: auto;
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    max-width: 100%;
    padding: ${(props) => (props.$pinImageRight ? '60px 0 0' : '80px 60px 0px 60px')};
  }

  ${MEDIA.mobileM} {
    padding: 40px 0 0;
  }
`;

const ContentContainer = styled.div<{ $centeredText: boolean }>`
  width: 700px;
  max-width: 45%;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$centeredText ? 'center' : 'flex-start')};
  justify-content: center;
  padding: 0;
  row-gap: 24px;
  color: ${Colors.navyBlue};
  z-index: 3;

  ${MEDIA.laptop} {
    position: relative;
    width: 100%;
    max-width: 100%;
    left: 0;
    bottom: -20px;
    align-items: center;
    text-align: center;
  }

  ${MEDIA.mobileL} {
    max-width: 85vw;
    margin: 0 auto;
  }
`;

const ComingSoonWrap = styled.div`
  display: flex;

  ${MEDIA.laptop} {
    justify-content: center;
  }
`;

const ImageContainer = styled.div<ImageProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  width: 700px;
  max-width: 57%;
  position: absolute;
  right: ${(props) => (props.$pinImageRight ? '0px' : '-24px')};
  bottom: 0px;

  > * {
    height: 100%;
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    object-fit: ${(props) => (props.$pinImageRight ? 'cover' : 'contain')};
  }

  ${MEDIA.laptop} {
    position: static;
    width: 100%;
    max-width: 100%;
    height: auto;

    img {
      position: static;
      width: 100%;
      height: 100%;
      max-height: ${(props) => (props.$pinImageRight ? 'auto' : '400px')};
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const WaitlistWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  gap: 10px;
`;

const FSAHSANotice = styled.p`
  ${TEXT_STYLES[TextVariant.UpperText3]}
  color: inherit;
`;

const OnlyFor = styled.div`
  margin-top: -8px;
  ${TEXT_STYLES[TextVariant.BodyRegularLG]}
  color: inherit;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  > p {
    font-size: 24px;
    color: inherit;
    margin-top: -2px;
  }
`;
